import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  LinearProgress,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { walletAPI } from '../services/api';

const formatCurrency = (n) => n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';

export default function CoverageLimits() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [limitsByPolicy, setLimitsByPolicy] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await walletAPI.listPolicies();
        const arr = Array.isArray(list) ? list : (list?.data ?? []);
        setPolicies(arr);
        const byPolicy = {};
        for (const p of arr) {
          try {
            const lim = await walletAPI.getLimits(p.id);
            const limits = Array.isArray(lim) ? lim : (lim?.data ?? []);
            byPolicy[p.id] = { policy: p, limits };
          } catch (_) {
            byPolicy[p.id] = { policy: p, limits: [] };
          }
        }
        setLimitsByPolicy(byPolicy);
      } catch (e) {
        console.warn(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <Container sx={{ py: 3 }}><Typography>Loading…</Typography></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Coverage Limits Dashboard</Typography>
      {policies.length === 0 ? (
        <Typography color="textSecondary">No policies. Add a policy from the wallet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(limitsByPolicy).map(([policyId, { policy, limits }]) => (
            <Card key={policyId}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">{policy.policy_type} ({policy.insurer_name})</Typography>
                {limits.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">No limits recorded</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {limits.map((row) => {
                      const limit = Number(row.annual_limit) || 0;
                      const used = Number(row.amount_used) || 0;
                      const remaining = Number(row.amount_remaining) ?? (limit - used);
                      const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
                      return (
                        <Box key={row.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{row.benefit_type}</Typography>
                            <Typography variant="body2">{pct.toFixed(0)}% · {formatCurrency(remaining)} left</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} color={pct >= 80 ? 'error' : pct >= 50 ? 'warning' : 'primary'} />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      <Typography component="button" variant="body2" sx={{ mt: 2, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/wallet')}>Back to Wallet</Typography>
    </Container>
  );
}
